import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatUSD, formatInt } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Gauge, MapPin, Phone, ChevronLeft, ChevronRight, Truck } from "lucide-react";

export default function EquipmentDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });

  useEffect(() => {
    setLoading(true);
    api.get(`/equipment/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  const nextImg = () => setActiveImg((i) => (i + 1) % (item?.images?.length || 1));
  const prevImg = () => setActiveImg((i) => (i - 1 + (item?.images?.length || 1)) % (item?.images?.length || 1));

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        lead_type: "quote",
        ...form,
        message: form.message || `Requesting quote for ${item.title}`,
        equipment_id: item.id,
        equipment_title: item.title,
      });
      toast.success("Quote request sent — our team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="mx-auto max-w-[1400px] p-10"><div className="h-96 animate-pulse border border-[#27272A] bg-[#141415]" /></div>;
  }
  if (!item) {
    return (
      <div className="mx-auto max-w-[1400px] p-10 text-center">
        <h1 className="font-display text-4xl font-black uppercase">Not Found</h1>
        <p className="mt-3 text-white/60">This machine may have just been sold.</p>
        <Link to="/inventory" className="mt-6 inline-block border border-[#27272A] px-6 py-3 font-display text-xs font-bold uppercase tracking-widest hover:border-[#FFB800] hover:text-[#FFB800]">Back to inventory</Link>
      </div>
    );
  }

  const images = item.images?.length ? item.images : ["https://images.pexels.com/photos/4040619/pexels-photo-4040619.jpeg"];

  return (
    <div data-testid="equipment-detail-page" className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
      <Link to="/inventory" className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-white/60 hover:text-[#FFB800]">
        <ArrowLeft className="h-4 w-4" /> Back to Inventory
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-12">
        {/* GALLERY */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] overflow-hidden border border-[#27272A] bg-black">
            <img src={images[activeImg]} alt={item.title} className="h-full w-full object-cover" />
            {images.length > 1 && (
              <>
                <button onClick={prevImg} data-testid="gallery-prev" className="absolute left-4 top-1/2 -translate-y-1/2 border border-white/20 bg-black/60 p-3 hover:border-[#FFB800]"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={nextImg} data-testid="gallery-next" className="absolute right-4 top-1/2 -translate-y-1/2 border border-white/20 bg-black/60 p-3 hover:border-[#FFB800]"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
            <div className="absolute bottom-4 right-4 border border-white/10 bg-black/70 px-3 py-1 font-mono text-[11px] uppercase tracking-widest">
              {activeImg + 1} / {images.length}
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-video overflow-hidden border ${i === activeImg ? "border-[#FFB800]" : "border-[#27272A]"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* SPECS */}
          <div className="mt-10 border border-[#27272A]">
            <div className="border-b border-[#27272A] bg-[#141415] p-5">
              <h2 className="font-display text-2xl font-bold uppercase">Specifications</h2>
            </div>
            <div className="grid grid-cols-1 gap-px bg-[#27272A] sm:grid-cols-2">
              {Object.entries(item.specs || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between bg-[#0a0a0b] p-4">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">{k}</span>
                  <span className="font-display text-sm font-bold uppercase text-white">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold uppercase">About this machine</h2>
            <p className="mt-4 text-base leading-relaxed text-white/70">{item.description}</p>
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 border border-[#27272A] bg-[#141415]">
            <div className="border-b border-[#27272A] p-6">
              <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFB800]">
                <span>{item.brand}</span>
                <span>{item.condition}</span>
              </div>
              <h1 data-testid="detail-title" className="mt-3 font-display text-3xl font-black uppercase leading-tight text-white">
                {item.title}
              </h1>
              <div className="mt-6 font-display text-5xl font-black text-[#FFB800]" data-testid="detail-price">
                {formatUSD(item.price)}
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[#27272A] pt-6">
                <Stat icon={Calendar} label="Year" value={item.year} />
                <Stat icon={Gauge} label="Hours" value={formatInt(item.hours)} />
                <Stat icon={MapPin} label="Location" value={item.location?.split(",")[0]} />
              </div>
              {item.vin && (
                <div className="mt-4 flex items-center justify-between border-t border-[#27272A] pt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">VIN / PIN</span>
                  <span data-testid="detail-vin" className="font-mono text-xs tracking-wider text-white">{item.vin}</span>
                </div>
              )}
              <div className="mt-4 flex items-start gap-2 border-t border-[#27272A] pt-4">
                <Truck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FFB800]" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB800]">Nationwide Delivery</div>
                  <div className="mt-1 text-xs text-white/70">We ship to any part of the United States — flatbed or lowboy, typically within 5 business days.</div>
                </div>
              </div>
            </div>

            <form onSubmit={submitQuote} data-testid="quote-form" className="p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Request a Quote</div>
              <h3 className="mt-2 font-display text-2xl font-bold uppercase">Talk to a dealer</h3>

              <div className="mt-5 space-y-3">
                {[
                  { k: "name", p: "Full Name *" },
                  { k: "email", p: "Email *", t: "email" },
                  { k: "phone", p: "Phone *", t: "tel" },
                  { k: "company", p: "Company (optional)" },
                ].map(({ k, p, t }) => (
                  <input
                    key={k}
                    data-testid={`quote-input-${k}`}
                    type={t || "text"}
                    placeholder={p}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full border border-[#27272A] bg-[#0a0a0b] p-3 font-mono text-sm text-white placeholder:text-white/30 focus:border-[#FFB800] focus:outline-none"
                  />
                ))}
                <textarea
                  data-testid="quote-input-message"
                  placeholder="Tell us about your project (optional)"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-[#27272A] bg-[#0a0a0b] p-3 font-mono text-sm text-white placeholder:text-white/30 focus:border-[#FFB800] focus:outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-quote-btn"
                className="mt-5 w-full bg-[#FFB800] px-6 py-4 font-display text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#E5A600] disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send Quote Request"}
              </button>

              <a href="tel:+18005550123" className="mt-3 flex w-full items-center justify-center gap-2 border border-[#27272A] px-6 py-3 font-display text-xs font-bold uppercase tracking-widest hover:border-[#FFB800] hover:text-[#FFB800]">
                <Phone className="h-4 w-4" /> Or call (800) 555-0123
              </a>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <Icon className="h-4 w-4 text-[#FFB800]" />
    <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
    <div className="mt-1 font-display text-lg font-bold text-white">{value}</div>
  </div>
);
